// src/features/system/auth/api/authApi.ts
import { fetchData } from '../../../../utils/fetch';

/* In-memory token store */
let ACCESS_TOKEN: string | null = null;
let REFRESH_TOKEN: string | null = null;

function setLocalTokens(access: string | null, refresh?: string | null) {
  try {
    if (access) localStorage.setItem('jwt', access);
    else localStorage.removeItem('jwt');

    if (refresh !== undefined) {
      if (refresh) localStorage.setItem('refreshToken', refresh);
      else localStorage.removeItem('refreshToken');
    }
  } catch {
    // ignore (private mode / disabled storage)
  }
}

function readLocalRefresh(): string | null {
  try {
    const v = localStorage.getItem('refreshToken');
    return v && v.trim() ? v : null;
  } catch {
    return null;
  }
}

export const auth = {
  get access()  { return ACCESS_TOKEN; },
  get refresh() { return REFRESH_TOKEN; },
  set(access: string | null, refresh?: string | null) {
    ACCESS_TOKEN = access ?? null;
    if (refresh !== undefined) REFRESH_TOKEN = refresh ?? null;

    // Keep fetch.ts expectations in sync.
    setLocalTokens(ACCESS_TOKEN, refresh);
  },
  clear() {
    ACCESS_TOKEN = null;
    REFRESH_TOKEN = null;
    setLocalTokens(null, null);
  },
};

/* ── helpers: always return a plain string->string map ─────────────── */
type HeadersDict = Record<string, string>;

function authHeader(): HeadersDict {
  return ACCESS_TOKEN ? { Authorization: `Bearer ${ACCESS_TOKEN}` } : {};
}
function withJson(h: HeadersDict = {}): HeadersDict {
  return { 'Content-Type': 'application/json', ...h };
}

/* DTOs */
export interface LoginPayload { username: string; password: string; }
export interface LoginResponse { access_token: string; refresh_token: string; }
export interface RegisterPayload { username: string; password: string; tenant_id: number; roles: string[]; }
export interface ChangePasswordPayload { old_password: string; new_password: string; }

/* Login */
export async function loginApi(payload: LoginPayload): Promise<LoginResponse> {
  const raw = await fetchData('/backend_services_rust/auth/login', {
    method: 'POST',
    headers: withJson(),
    body: JSON.stringify(payload),
    skipAuth: true,
  });
  const data = (raw && typeof raw === 'object' && 'data' in raw) ? (raw as any).data : raw;
  if (typeof data?.access_token === 'string' && typeof data?.refresh_token === 'string') {
    auth.set(data.access_token, data.refresh_token);
    return { access_token: data.access_token, refresh_token: data.refresh_token };
  }
  throw new Error('Could not parse login response.');
}

/* Register */
export async function registerApi(payload: RegisterPayload): Promise<{ id_user: number }> {
  const raw = await fetchData('/backend_services_rust/auth/register', {
    method: 'POST',
    headers: withJson(),
    body: JSON.stringify(payload),
    skipAuth: true,
  });
  const data = (raw && typeof raw === 'object' && 'created' in raw) ? (raw as any).created : raw;
  if (typeof data?.id_user === 'number') return { id_user: data.id_user };
  throw new Error('Could not parse register response.');
}

/* Refresh */
export async function refreshTokenApi(): Promise<{ access_token: string }> {
  if (!REFRESH_TOKEN) {
    // Support refresh calls initiated by fetch.ts which reads refreshToken from localStorage.
    // (Keeps this module usable even if the in-memory store was cleared by reload.)
    REFRESH_TOKEN = readLocalRefresh();
  }
  if (!REFRESH_TOKEN) throw new Error('No refresh token available');
  const raw = await fetchData('/backend_services_rust/auth/refresh', {
    method: 'POST',
    headers: withJson(),
    body: JSON.stringify({ refresh_token: REFRESH_TOKEN }),
    skipAuth: true,
  });
  const data = (raw && typeof raw === 'object' && 'data' in raw) ? (raw as any).data : raw;
  if (typeof data?.access_token === 'string') {
    auth.set(data.access_token); // keep existing refresh
    return { access_token: data.access_token };
  }
  throw new Error('Could not parse refresh response.');
}

/* Logout */
export async function logoutApi(): Promise<void> {
  try {
    await fetchData('/backend_services_rust/auth/logout', {
      method: 'POST',
      headers: withJson(authHeader()),
    });
  } finally {
    auth.clear();
  }
}

/* Password helpers */
export async function changePasswordApi(payload: ChangePasswordPayload): Promise<void> {
  await fetchData('/backend_services_rust/auth/change_password', {
    method: 'POST',
    headers: withJson(authHeader()),
    body: JSON.stringify(payload),
  });
}

export async function resetPasswordApi(): Promise<{ new_password: string }> {
  const raw = await fetchData('/backend_services_rust/auth/reset_password', {
    method: 'POST',
    headers: withJson(authHeader()),
  });
  const data = (raw && typeof raw === 'object' && 'data' in raw) ? (raw as any).data : raw;
  if (typeof data?.new_password === 'string') return { new_password: data.new_password };
  throw new Error('Could not parse reset response.');
}

/**
 * Start explicit OIDC link flow for the currently authenticated PAC user.
 *
 * We call the link-start endpoint with bearer auth and `redirect: 'manual'`
 * to capture the upstream provider Location header, then navigate the browser.
 * This avoids implicit email auto-link and keeps linking intentional.
 */
export async function startGoogleLinkFlow(): Promise<void> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => {
    try { ctrl.abort(); } catch {}
  }, 12_000);

  // Session-first flow: let BFF read appJwt from the server-side session and
  // relay the provider redirect. This avoids brittle dependence on localStorage
  // JWT mirrors and works reliably in cookie-auth mode.
  let res: Response;
  try {
    res = await fetch('/bff/auth/oidc/google/link/start', {
      method: 'GET',
      credentials: 'include',
      redirect: 'manual',
      signal: ctrl.signal,
    });
  } catch (err: any) {
    // If the preflight/manual fetch stalls or is blocked, force a top-level
    // navigation so the browser can follow server redirects directly.
    if (err?.name === 'AbortError') {
      window.location.assign('/bff/auth/oidc/google/link/start');
      return;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  const location = res.headers.get('location');
  if (location && res.status >= 300 && res.status < 400) {
    const absolute = new URL(location, window.location.origin).toString();
    window.location.assign(absolute);
    return;
  }

  // Some browser/runtime combinations hide redirect headers on manual redirect
  // responses; fall back to top-level navigation so the redirect chain proceeds.
  if (res.status >= 300 && res.status < 400) {
    window.location.assign('/bff/auth/oidc/google/link/start');
    return;
  }

  // Opaque manual-redirect responses (common when redirecting cross-origin)
  // intentionally hide status/headers and surface as status=0.
  if (res.type === 'opaqueredirect' || res.status === 0) {
    window.location.assign('/bff/auth/oidc/google/link/start');
    return;
  }

  if (res.status === 401 || res.status === 403) {
    throw new Error('Session expired or not authorized. Please sign in again.');
  }

  throw new Error(`Could not start Google link flow (status ${res.status || 'unknown'}).`);
}

export interface GoogleLinkStatus {
  linked: boolean | null;
  reason: string;
  email?: string | null;
  provider?: string | null;
  checkedAt: number;
}

export interface LinkedIdentitiesResponse {
  /** Whether a Google account is actively linked. */
  google_linked: boolean;
  /** Email on the linked Google account (null if not linked). */
  google_email: string | null;
  /** Full provider list from the backend — stable typed contract. */
  providers: Array<{
    name: string;
    email: string | null;
    linked_at: string | null;
  }>;
  /** Epoch ms when the BFF fetched this from the backend — use for freshness badge. */
  fetched_at: number;
  /**
   * Soft-endpoint contract: the BFF returns 200 even when the underlying
   * call could not produce a definitive answer (no session, refresh failed,
   * upstream unreachable, …). When `degraded === true` the FE should
   * render a "STATUS UNKNOWN" / "Sign in again to view link status" hint
   * keyed off `reason`, but MUST NOT force a logout.
   * See `bff-fe/server/bff/lib/session-contract.mjs` for the full contract.
   */
  degraded?: boolean;
  reason?:
    | 'session_absent'
    | 'session_expired'
    | 'missing_session_token'
    | 'refresh_rejected'
    | 'upstream_not_found'
    | 'upstream_unauthorized'
    | 'upstream_error'
    | 'upstream_timeout'
    | 'forbidden'
    | 'feature_not_enabled';
}

/**
 * Fetch linked federated identities for the current authenticated user.
 *
 * Soft endpoint: the BFF always returns 200 — either a populated provider
 * list or a typed `{ degraded: true, reason }` envelope. The FE must
 * surface degraded state in the UI but NEVER trigger a global logout.
 */
export async function getLinkedIdentities(): Promise<LinkedIdentitiesResponse | null> {
  try {
    const res = await fetch('/bff/auth/linked-identities', {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    // Per the soft-endpoint contract this should always be 200; treat any
    // non-2xx as an unknown degraded state instead of throwing.
    if (!res.ok) {
      return {
        google_linked: false,
        google_email: null,
        providers: [],
        fetched_at: Date.now(),
        degraded: true,
        reason: 'upstream_error',
      };
    }
    const data = await res.json();
    return {
      google_linked: data?.google_linked === true,
      google_email: typeof data?.google_email === 'string' ? data.google_email : null,
      providers: Array.isArray(data?.providers) ? data.providers : [],
      fetched_at: typeof data?.fetched_at === 'number' ? data.fetched_at : Date.now(),
      degraded: data?.degraded === true,
      reason: typeof data?.reason === 'string' ? data.reason : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Best-effort status probe for explicit Google account linking.
 * @deprecated Use `getLinkedIdentities()` for the clean typed contract.
 * This shim delegates to the new endpoint for backwards compatibility.
 */
export async function getGoogleLinkStatus(): Promise<GoogleLinkStatus> {
  const checkedAt = Date.now();
  const result = await getLinkedIdentities();
  if (!result) {
    return { linked: null, reason: 'status-unavailable', checkedAt };
  }
  return {
    linked: result.google_linked,
    reason: result.google_linked ? 'direct-provider-hint' : 'no-google-indicator',
    email: result.google_email,
    provider: result.google_linked ? 'google' : null,
    checkedAt: result.fetched_at,
  };
}

/**
 * Admin-scoped fetch of linked federated identities for ANOTHER user.
 *
 * Used by Admin → IAM → Users to display per-user federation visibility.
 * The BFF enforces caller perms (iam.users.read / iam.users.list /
 * superadmin / admin), and the Rust backend re-enforces the same gate.
 *
 * Returns null on auth failure or upstream unavailability — caller renders
 * a neutral "unknown" badge rather than crashing the row.
 */
export interface AdminUserLinkedProvidersResponse extends LinkedIdentitiesResponse {
  id_user: number;
}

export async function getAdminUserLinkedProviders(
  idUser: number,
): Promise<AdminUserLinkedProvidersResponse | null> {
  if (!Number.isFinite(idUser) || idUser <= 0) return null;
  try {
    const res = await fetch(`/bff/auth/admin/users/${idUser}/linked-providers`, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      id_user: typeof data?.id_user === 'number' ? data.id_user : idUser,
      google_linked: data?.google_linked === true,
      google_email: typeof data?.google_email === 'string' ? data.google_email : null,
      providers: Array.isArray(data?.providers) ? data.providers : [],
      fetched_at: typeof data?.fetched_at === 'number' ? data.fetched_at : Date.now(),
    };
  } catch {
    return null;
  }
}
